/**
 * training-groups-menu.js
 */

import React from 'react';
import {
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { Alert } from '@mui/material';
import { useHistory, useParams, matchPath, Link } from 'react-router-dom';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import { DogsContext } from 'contexts/dogs/dogs-context';
import DrawerMenu from 'components/menu-drawer/menu/drawer-menu';
import DrawerMenuItem from 'components/menu-drawer/menu/drawer-menu-item';
import ScrollableList from 'components/scrollable-list';
import Icon from 'components/icon';
import AppBar from 'components/app-bar';
import CaptionedSvg from 'components/captioned-svg';
import RequestType from '../request-type';
import { ConnectedHandlersContext } from '../connections/connected-handlers-context';
import { TrainingGroupsContext } from './training-groups-context';
import { trainingGroupRoute, newTrainingGroup } from './training-groups';
import TrainingGroupAdd from './training-group-add';
import { ReactComponent as GroupsImage } from 'assets/images/groups.svg';
import { ReactComponent as TeamCollabSvg } from 'assets/images/team-collaboration.svg';


export default function TrainingGroupMenu(props) {
  const { basePath } = props;
  const { groups } = React.useContext(TrainingGroupsContext);
  const { managedHandlerIds } = React.useContext(ConnectedHandlersContext)
  const { user } = React.useContext(UserContext);
  const { getActiveDogs } = React.useContext(DogsContext);
  const isMobileDrawer = useMediaQuery(theme => theme.breakpoints.down('sm_md'), { noSsr: true });
  const history = useHistory();
  const params = useParams();
  const groupId = +params.id;
  const isInvalidTrainer = user.role.isTrainer && managedHandlerIds?.length === 0;
  const relevantGroups = [
    ...(groupId === 0 ? [newTrainingGroup(user, getActiveDogs())] : []),
    ...groups.filter(g => isMobileDrawer || user.role.isTrainer || g.membership.dateAccepted || g.membership.type === Types.MemberRequest.TO_USER)
  ].map(g => {
    const to = trainingGroupRoute(g.id, history);
    const isActive = Boolean(matchPath(history.location.pathname, { path: to.pathname, exact: true }));
    return { ...g, to, isActive };
  });


  const closeButton = (
    <IconButton
      data-testid="back-button"
      size="small"
      style={{color: 'inherit'}}
      component={Link}
      to={basePath}
      children={<Icon type='mobile-close-drawer' />}
    />
  );


  const addGroupButton = (
    <TrainingGroupAdd
      menuLabel={<IconButton
        data-testid='add-training-group'
        size='small'
        children={<Icon type='add' />}
      />}
    />
  )


  const menuItems = <>
    {relevantGroups.map(g => (
      <DrawerMenuItem
        key={`training-menu-${g.id}`}
        to={g.to}
        isActive={g.isActive}
        iconName='group-training'
        label={g.name}
        labelEnd={g.id === 0
          ? ''
          : user.role.isTrainer || g.membership?.dateAccepted 
            ? g.members?.length || ''
            : <RequestType context='training-group' request={g.membership} />}
      />
    ))}

    {isInvalidTrainer &&
    <Alert variant='standard' severity='warning' style={{ margin: '0 24px' }}>
      You must be training at least one handler in order to manage training groups. Invite a handler to train from the Manage page.
    </Alert>}

    {isMobileDrawer && 
    <CaptionedSvg
      image={<TeamCollabSvg />}
      text={<>Use the <b>+</b> button to get connected with a group of handlers.</>}
    />}
  </>;


  return (
    <DrawerMenu
      header={isMobileDrawer && <AppBar title='TRAINING GROUPS' leftActions={closeButton} rightActions={addGroupButton} />}
      footer={!isMobileDrawer && <GroupsImage />}
      hideDivider={relevantGroups.length === 0}
      menuGroups={[{
        name: '',
        component: ScrollableList,
        scrollable: true,
        items: menuItems
      }]}
    />
  );
}